import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, mainSiteUrl } from '@/mocks/categories';
import { SITE_URL as siteUrl } from '@/mocks/siteConfig';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);
  const articleDay = article ? String(parseInt(article.date) || 15).padStart(2, '0') : '15';
  const articleMonth = article?.date?.includes('Lipca') ? '07' : '01';

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-background-100 rounded-full mx-auto mb-4">
              <i className="ri-error-warning-line text-2xl text-foreground-300"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">Artykuł nie znaleziony</h1>
            <p className="text-sm text-foreground-500 mb-6">Przepraszamy, artykuł którego szukasz nie istnieje lub został usunięty.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
            >
              <i className="ri-home-line w-4 h-4 flex items-center justify-center"></i>
              Wróć na stronę główną
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleUrl = `${siteUrl}/artykul/${article.slug}`;

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    datePublished: `2026-07-${articleDay}T08:00:00+02:00`,
    dateModified: `2026-07-${articleDay}T08:00:00+02:00`,
    author: {
      '@type': 'Organization',
      name: 'KakobuyPolska',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'KakobuyPolska',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8caf559f256f.png',
      },
    },
    image: article.image,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Kakobuy Polska Guide',
        item: siteUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Poradniki',
        item: `${siteUrl}/#artykuly`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />

      <main className="pt-20">
        <article className="max-w-4xl mx-auto px-4 md:px-6 py-10 md:py-16">
          <nav className="mb-8" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-sm text-foreground-400 flex-wrap">
              <li>
                <Link to="/" className="hover:text-primary-500 transition-colors">Strona główna</Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-xs"></i>
              </li>
              <li>
                <Link to="/#artykuly" className="hover:text-primary-500 transition-colors">Poradniki</Link>
              </li>
              <li>
                <i className="ri-arrow-right-s-line text-xs"></i>
              </li>
              <li className="text-foreground-600 font-medium truncate max-w-[200px]">{article.title}</li>
            </ol>
          </nav>

          <header className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full">{article.tag}</span>
              <span className="text-xs text-foreground-400">{article.date}</span>
            </div>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 leading-tight mb-6">
              {article.title}
            </h1>
            <p className="text-base md:text-lg text-foreground-500 leading-relaxed max-w-3xl">
              {article.description}
            </p>

            <div className="flex items-center gap-4 mt-6 pt-6 border-t border-background-200">
              <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center">
                <i className="ri-user-line text-accent-600"></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground-800">Zespół KakobuyPolska</p>
                <p className="text-xs text-foreground-400">Niezależny przewodnik informacyjny</p>
              </div>
            </div>
          </header>

          <div className="relative rounded-2xl overflow-hidden mb-12">
            <img
              src={article.image}
              alt={article.title}
              className="w-full aspect-[16/10] object-cover"
            />
          </div>

          <div className="prose-custom space-y-8">
            {article.content.map((block, i) => {
              if (block.type === 'h2') {
                return (
                  <section key={i}>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                      {block.text}
                    </h2>
                  </section>
                );
              }
              if (block.type === 'p') {
                return (
                  <section key={i}>
                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                      {block.text}
                    </p>
                  </section>
                );
              }
              return null;
            })}
          </div>

          <div className="bg-background-100 rounded-2xl p-8 border border-background-200 mt-12">
            <h3 className="font-heading text-xl font-bold text-foreground-900 mb-3">
              Chcesz dowiedzieć się więcej?
            </h3>
            <p className="text-sm text-foreground-600 mb-6">
              Odkryj więcej poradników i analiz na naszej stronie. Sprawdź Kakobuy Spreadsheet i znajdź najlepsze produkty już teraz.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer text-sm text-center justify-center"
              >
                <i className="ri-external-link-line w-4 h-4 flex items-center justify-center"></i>
                Sprawdź Kakobuy Spreadsheet
              </a>
              <Link
                to="/#artykuly"
                className="inline-flex items-center gap-2 px-6 py-3 bg-background-50 hover:bg-background-200 text-foreground-700 font-medium rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer text-sm text-center justify-center border border-background-300"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                Więcej poradników
              </Link>
            </div>
          </div>
        </article>

        <section className="bg-background-100 py-10 md:py-14">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6 text-center">
              Inne artykuły, które mogą Cię zainteresować
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {articles
                .filter((a) => a.slug !== slug)
                .slice(0, 3)
                .map((a) => (
                  <Link
                    key={a.slug}
                    to={`/artykul/${a.slug}`}
                    className="group p-5 bg-background-50 rounded-xl border border-background-200 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="w-10 h-10 flex items-center justify-center bg-accent-100 rounded-lg mb-3 group-hover:bg-accent-200 transition-colors">
                      <i className="ri-article-line text-accent-600"></i>
                    </div>
                    <h3 className="font-heading text-sm font-semibold text-foreground-800 mb-1 line-clamp-2">{a.title}</h3>
                    <p className="text-xs text-foreground-400">{a.date}</p>
                  </Link>
                ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}